import { filterValuesMatch } from '../../features/assets/asset-library-search-state.util';
import {
  normalizeTaxonomyFilterValues,
  taxonomyValuesOverlap,
} from '../../features/assets/asset-taxonomy.util';
import { ASSET_SEARCH_VISIBILITY_OPTIONS } from '../../features/assets/constants/asset-taxonomies';
import type {
  AssetAssistantAppliedFilterDto,
  AssetAssistantAssetResultDto,
  AssetAssistantRequestBody,
  AssetAssistantResponseDto,
  AssetAssistantSearchStateDto,
  AssetAssistantSnippetResultDto,
  AssetAssistantSuggestedFilterDto,
} from '../../features/assets/models/asset-assistant-api.models';
import { mockIndustryDtosFromNames } from './industries-api-mock.handlers';
import { mockCatalogUserFromEmail } from './users-api-mock.fixtures';
import type {
  AssetSearchVisibilityApi,
  AssetCatalogEntryDto,
  AssetCatalogListResponseDto,
  AssetTypeApi,
} from '../../features/assets/models/asset-api.models';

interface MockLibraryCatalogEntry {
  asset: AssetAssistantAssetResultDto;
  snippets: AssetAssistantSnippetResultDto[];
  searchText: string;
}

export function findMockLibraryAssetById(assetId: string): AssetAssistantAssetResultDto | null {
  const entry = MOCK_LIBRARY_CATALOG.find((row) => row.asset.assetId === assetId);
  return entry?.asset ?? null;
}

const MOCK_LIBRARY_CATALOG: MockLibraryCatalogEntry[] = [
  {
    asset: mockAsset(
      'asset-mock-001',
      'Cloud platform modernisation — client proposal',
      'Proposal',
      'Transport',
      'Digital Solutions',
      ['cloud', 'migration', 'proposal'],
      true,
    ),
    snippets: [
      mockSnippet(
        'asset-mock-001',
        'Migration phases',
        'Phase 1 establishes landing zone and security baseline; phase 2 migrates workloads in waves.',
      ),
    ],
    searchText: 'cloud migration proposal transportation powerpoint',
  },
  {
    asset: mockAsset(
      'asset-mock-002',
      'Data & AI capability overview',
      'Offering',
      'Others',
      'Data & AI',
      ['AI', 'analytics', 'capabilities'],
      true,
    ),
    snippets: [
      mockSnippet(
        'asset-mock-002',
        'Responsible AI',
        'Our responsible AI framework covers governance, model risk, and client-specific guardrails.',
      ),
    ],
    searchText: 'data ai offering analytics capabilities pdf',
  },
  {
    asset: mockAsset(
      'asset-mock-003',
      'Regulatory compliance delivery report',
      'DeliveryOutput',
      'FSI - Banking',
      'Digital Consulting',
      ['compliance', 'FINMA', 'delivery'],
      true,
    ),
    snippets: [
      mockSnippet(
        'asset-mock-003',
        'Audit trail',
        'Every control mapping links to evidence artefacts stored in the client compliance repository.',
      ),
    ],
    searchText: 'compliance banking finma delivery report word',
  },
  {
    asset: mockAsset(
      'asset-mock-004',
      'Zero-trust architecture blueprint',
      'ArchitectureBlueprint',
      'Health',
      'Cyber Security',
      ['security', 'zero-trust', 'architecture'],
      true,
    ),
    snippets: [
      mockSnippet(
        'asset-mock-004',
        'Identity layer',
        'Identity is the control plane: conditional access, MFA, and privileged access management are mandatory.',
      ),
    ],
    searchText: 'architecture blueprint security zero trust health golden',
  },
  {
    asset: mockAsset(
      'asset-mock-005',
      'Managed services TCO calculator',
      'Calculator',
      'Retail',
      'Managed Services',
      ['TCO', 'managed services'],
      true,
    ),
    snippets: [],
    searchText: 'calculator tco retail managed services excel',
  },
  {
    asset: mockAsset(
      'asset-mock-006',
      'Energy sector reference case study',
      'Reference',
      'Energy',
      'Digital Solutions',
      ['energy', 'reference', 'case study'],
      true,
    ),
    snippets: [
      mockSnippet(
        'asset-mock-006',
        'Outcomes',
        'Reduced outage minutes by 35% and accelerated release cadence from quarterly to bi-weekly.',
      ),
    ],
    searchText: 'energy reference case study golden pdf',
  },
  {
    asset: mockAsset(
      'asset-mock-007',
      'Public sector PoV — sovereign cloud',
      'PointOfView',
      'Public',
      'Digital Consulting',
      ['public', 'sovereign', 'cloud'],
      true,
    ),
    snippets: [],
    searchText: 'public point of view sovereign cloud',
  },
  {
    asset: mockAsset(
      'asset-mock-008',
      'Insurance claims automation accelerator',
      'Accelerator',
      'FSI - Insurance',
      'Data & AI',
      ['insurance', 'automation'],
      true,
    ),
    snippets: [
      mockSnippet(
        'asset-mock-008',
        'Accelerator scope',
        'Pre-built connectors for policy admin systems and a starter ML pipeline for claims triage.',
      ),
    ],
    searchText: 'insurance accelerator automation golden ppt',
  },
  {
    asset: mockAsset(
      'asset-mock-draft-001',
      'Draft — legacy portal rewrite proposal',
      'Proposal',
      'FSI - Insurance',
      'Digital Solutions',
      ['draft', 'portal'],
      false,
    ),
    snippets: [],
    searchText: 'draft insurance portal proposal',
  },
];

function mockAsset(
  assetId: string,
  title: string,
  type: AssetTypeApi,
  industries: string | string[],
  practices: string | string[],
  tags: string[],
  isPublished: boolean,
): AssetAssistantAssetResultDto {
  return {
    assetId,
    assetVersionId: `${assetId}-v1`,
    title,
    summary: `Mock summary for ${title}.`,
    type,
    industries: Array.isArray(industries) ? industries : [industries],
    practices: Array.isArray(practices) ? practices : [practices],
    tags,
    owner: mockCatalogUserFromEmail('mock.author@zuehlke.com'),
    confidentiality: 'Internal',
    score: 0.81,
    matchedReason: 'Matched indexed content (mock).',
    itemVersionsSummary: '1 file',
    isPublished,
    hasPendingAiSuggestions: false,
  };
}

function mockSnippet(
  assetId: string,
  title: string,
  contentPreview: string,
): AssetAssistantSnippetResultDto {
  return {
    assetId,
    assetVersionId: `${assetId}-v1`,
    assetItemVersionId: `${assetId}-item-1`,
    chunkId: `${assetId}-chunk-${title.toLowerCase().replace(/\s+/g, '-')}`,
    title,
    contentPreview,
    score: 0.88,
    sectionPath: ['Body'],
    fileName: 'document.pdf',
  };
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function matchesFilters(
  entry: MockLibraryCatalogEntry,
  state: AssetAssistantSearchStateDto,
): boolean {
  if (state.types && state.types.length > 0) {
    const typeStrings = state.types.map(String);
    if (!typeStrings.includes(entry.asset.type)) return false;
  }

  if (!taxonomyValuesOverlap(entry.asset.industries, state.industries)) {
    return false;
  }

  if (!taxonomyValuesOverlap(entry.asset.practices, state.practices)) {
    return false;
  }

  if (state.tags && state.tags.length > 0) {
    const assetTags = entry.asset.tags.map((tag) => normalize(tag));
    const allMatch = state.tags.every((tag) =>
      assetTags.some((assetTag) => assetTag.includes(normalize(tag))),
    );
    if (!allMatch) return false;
  }

  return true;
}

function scoreEntry(entry: MockLibraryCatalogEntry, query: string): number {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return entry.asset.score;

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  let score = entry.asset.score;
  for (const token of tokens) {
    if (entry.searchText.includes(token)) score += 0.05;
  }
  return score;
}

function matchesVisibility(
  entry: MockLibraryCatalogEntry,
  visibility: AssetSearchVisibilityApi,
): boolean {
  const isPublished = entry.asset.isPublished;
  switch (visibility) {
    case 'DraftsOnly':
      return !isPublished;
    case 'IncludeDrafts':
      return true;
    case 'PublishedOnly':
    default:
      return isPublished;
  }
}

function runMockSearch(
  query: string,
  state: AssetAssistantSearchStateDto,
  size: number,
  visibility: AssetSearchVisibilityApi,
): { assets: AssetAssistantAssetResultDto[]; snippets: AssetAssistantSnippetResultDto[] } {
  const ranked = MOCK_LIBRARY_CATALOG.filter((entry) => {
    if (!matchesVisibility(entry, visibility)) return false;
    return matchesFilters(entry, state);
  })
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .sort((left, right) => right.score - left.score)
    .slice(0, size);

  const assets = ranked.map((item) => ({ ...item.entry.asset, score: item.score }));
  const snippets = ranked.flatMap((item) => item.entry.snippets);
  return { assets, snippets };
}

function effectiveMockVisibility(state: AssetAssistantSearchStateDto): AssetSearchVisibilityApi {
  return state.visibility ?? 'PublishedOnly';
}

function visibilityChipLabel(visibility: AssetSearchVisibilityApi): string {
  return (
    ASSET_SEARCH_VISIBILITY_OPTIONS.find((option) => option.value === visibility)?.label ??
    visibility
  );
}

function buildAppliedFilters(
  state: AssetAssistantSearchStateDto,
): AssetAssistantAppliedFilterDto[] {
  const applied: AssetAssistantAppliedFilterDto[] = [];
  const visibility = effectiveMockVisibility(state);

  if (visibility !== 'PublishedOnly') {
    applied.push({
      key: 'visibility',
      label: 'Visibility',
      value: visibilityChipLabel(visibility),
    });
  }

  for (const type of state.types ?? []) {
    applied.push({ key: 'type', label: 'Type', value: String(type) });
  }
  for (const industry of normalizeTaxonomyFilterValues(state.industries)) {
    applied.push({ key: 'industry', label: 'Industry', value: industry });
  }
  for (const practice of normalizeTaxonomyFilterValues(state.practices)) {
    applied.push({ key: 'practice', label: 'Practice', value: practice });
  }

  return applied;
}

function isTypeApplied(state: AssetAssistantSearchStateDto, type: AssetTypeApi): boolean {
  return (state.types ?? []).some((value) => filterValuesMatch(String(value), type));
}

function isIndustryApplied(state: AssetAssistantSearchStateDto, industry: string): boolean {
  return normalizeTaxonomyFilterValues(state.industries).some((value) =>
    filterValuesMatch(value, industry),
  );
}

function isPracticeApplied(state: AssetAssistantSearchStateDto, practice: string): boolean {
  return normalizeTaxonomyFilterValues(state.practices).some((value) =>
    filterValuesMatch(value, practice),
  );
}

function pushSuggestedFilter(
  suggested: AssetAssistantSuggestedFilterDto[],
  entry: AssetAssistantSuggestedFilterDto,
): void {
  const exists = suggested.some(
    (item) => item.key === entry.key && filterValuesMatch(item.value, entry.value),
  );
  if (!exists) {
    suggested.push(entry);
  }
}

/** Soft keyword hints → dashed chips; explicit "only …" phrases auto-apply in inferStateFromMessage. */
function buildSuggestedFilters(
  message: string,
  state: AssetAssistantSearchStateDto,
): AssetAssistantSuggestedFilterDto[] {
  const normalized = message.trim().toLowerCase();
  const suggested: AssetAssistantSuggestedFilterDto[] = [];

  if (normalized.includes('insurance') && !isIndustryApplied(state, 'FSI - Insurance')) {
    pushSuggestedFilter(suggested, {
      key: 'industry',
      label: 'Industry',
      value: 'FSI - Insurance',
      reason: 'Insurance mentioned in your query',
      confidence: 0.72,
    });
  }

  if (normalized.includes('energy') && !isIndustryApplied(state, 'Energy')) {
    pushSuggestedFilter(suggested, {
      key: 'industry',
      label: 'Industry',
      value: 'Energy',
      reason: 'Energy sector mentioned in your query',
      confidence: 0.7,
    });
  }

  if (
    (normalized.includes('banking') || /\bbank\b/.test(normalized)) &&
    !isIndustryApplied(state, 'FSI - Banking')
  ) {
    pushSuggestedFilter(suggested, {
      key: 'industry',
      label: 'Industry',
      value: 'FSI - Banking',
      reason: 'Banking mentioned in your query',
      confidence: 0.68,
    });
  }

  if (
    (normalized.includes('proposal') || normalized.includes('rfp')) &&
    !isTypeApplied(state, 'Proposal')
  ) {
    pushSuggestedFilter(suggested, {
      key: 'type',
      label: 'Type',
      value: 'Proposal',
      reason: 'Proposal content mentioned in your query',
      confidence: 0.68,
    });
  }

  if (
    (normalized.includes('reference') || normalized.includes('case study')) &&
    !isTypeApplied(state, 'Reference')
  ) {
    pushSuggestedFilter(suggested, {
      key: 'type',
      label: 'Type',
      value: 'Reference',
      reason: 'Reference material mentioned in your query',
      confidence: 0.65,
    });
  }

  if (
    (normalized.includes('data & ai') ||
      normalized.includes('data and ai') ||
      normalized.includes('analytics')) &&
    !isPracticeApplied(state, 'Data & AI')
  ) {
    pushSuggestedFilter(suggested, {
      key: 'practice',
      label: 'Practice',
      value: 'Data & AI',
      reason: 'Data & AI mentioned in your query',
      confidence: 0.71,
    });
  }

  if (
    (normalized.includes('cyber') || normalized.includes('security')) &&
    !isPracticeApplied(state, 'Cyber Security')
  ) {
    pushSuggestedFilter(suggested, {
      key: 'practice',
      label: 'Practice',
      value: 'Cyber Security',
      reason: 'Security topic mentioned in your query',
      confidence: 0.66,
    });
  }

  if (
    effectiveMockVisibility(state) === 'PublishedOnly' &&
    (normalized.includes('draft') || normalized.includes('unpublished'))
  ) {
    pushSuggestedFilter(suggested, {
      key: 'visibility',
      label: 'Visibility',
      value: visibilityChipLabel('IncludeDrafts'),
      reason: 'Drafts mentioned in your query',
      confidence: 0.68,
    });
  }

  return suggested;
}

function inferStateFromMessage(
  message: string,
  current: AssetAssistantSearchStateDto | null | undefined,
): AssetAssistantSearchStateDto {
  const normalized = message.trim().toLowerCase();
  const state: AssetAssistantSearchStateDto = {
    visibility: 'PublishedOnly',
    query: message.trim(),
    types: current?.types ? [...current.types] : null,
    industries: current?.industries ? [...current.industries] : null,
    practices: current?.practices ? [...current.practices] : null,
    tags: current?.tags ? [...current.tags] : null,
    mimeTypes: current?.mimeTypes ? [...current.mimeTypes] : null,
    intent: current?.intent ?? 'Browse',
    retrievalMode: current?.retrievalMode ?? 'Broad',
  };

  // Explicit filter commands auto-apply (solid chips). Soft keywords only suggest.
  if (/\b(only proposals?|proposals? only)\b/.test(normalized)) {
    state.types = ['Proposal'];
  }
  if (/\b(only insurance|insurance only)\b/.test(normalized)) {
    state.industries = ['FSI - Insurance'];
  }
  if (normalized.includes('pdf')) {
    state.mimeTypes = ['application/pdf'];
  }
  if (/\b(drafts? only|only drafts?)\b/.test(normalized)) {
    state.visibility = 'DraftsOnly';
  } else if (/\b(include drafts?|with drafts?)\b/.test(normalized)) {
    state.visibility = 'IncludeDrafts';
  } else if (/\b(published only|only published)\b/.test(normalized)) {
    state.visibility = 'PublishedOnly';
  }

  return state;
}

function buildMockResponse(
  query: string,
  state: AssetAssistantSearchStateDto,
  size: number,
  options: {
    assistantMessage: string;
    intent: AssetAssistantResponseDto['intent'];
    retrievalMode: AssetAssistantResponseDto['retrievalMode'];
    suggestedFilters: AssetAssistantSuggestedFilterDto[];
    includeSnippets: boolean;
  },
): AssetAssistantResponseDto {
  const visibility = effectiveMockVisibility(state);
  const { assets, snippets } = runMockSearch(query, state, size, visibility);
  const supplemental =
    visibility === 'PublishedOnly' ? runMockSearch(query, state, 3, 'DraftsOnly').assets : [];

  return {
    assistantMessage: options.assistantMessage,
    intent: options.intent,
    retrievalMode: options.retrievalMode,
    semanticQuery: query,
    appliedFilters: buildAppliedFilters(state),
    suggestedFilters: options.suggestedFilters,
    assetResults: options.includeSnippets ? assets.slice(0, 3) : assets,
    supplementalDraftResults: supplemental,
    snippetResults: options.includeSnippets ? snippets : [],
    followUpSuggestions: [
      { text: 'Cloud migration proposal content' },
      { text: 'Insurance related proposals' },
      { text: 'High-quality reference assets' },
    ],
    searchState: {
      ...state,
      query,
      appliedFilters: buildAppliedFilters(state),
    },
  };
}

export function mockHandleAssetsAssistantPost(body: unknown): {
  status: number;
  body: AssetAssistantResponseDto;
} {
  const request = body as AssetAssistantRequestBody;
  const size = request?.size && request.size > 0 ? request.size : 12;

  if (request?.interactionType === 'FilterChanged') {
    const state = request.currentState;
    const query = state?.query?.trim() ?? '';
    if (!state || !query) {
      return {
        status: 400,
        body: {} as AssetAssistantResponseDto,
      };
    }

    const response = buildMockResponse(query, state, size, {
      assistantMessage: 'Results updated based on your filters.',
      intent: state.intent ?? 'Browse',
      retrievalMode: state.retrievalMode ?? 'Broad',
      suggestedFilters: [],
      includeSnippets: state.retrievalMode === 'Snippet',
    });

    return { status: 200, body: response };
  }

  const message = request?.message?.trim() ?? '';
  const mergedState = inferStateFromMessage(message, request?.currentState ?? null);
  const query = mergedState.query ?? message;
  const isCompose =
    normalizedContains(message, 'snippet') ||
    normalizedContains(message, 'paragraph') ||
    normalizedContains(message, 'wording');

  const suggestedFilters = buildSuggestedFilters(message, mergedState);
  const publishedCount = runMockSearch(
    query,
    mergedState,
    size,
    effectiveMockVisibility(mergedState),
  ).assets.length;
  const suggestionHint =
    suggestedFilters.length > 0 ? ' Dashed filter chips are suggestions — click to apply.' : '';

  const response = buildMockResponse(query, mergedState, size, {
    assistantMessage: isCompose
      ? `I found matching content across ${publishedCount} assets for "${query}" (mock).${suggestionHint}`
      : publishedCount > 0
        ? `I found ${publishedCount} assets related to "${query}" (mock).${suggestionHint}`
        : `I could not find published assets matching "${query}" (mock). Try different keywords.${suggestionHint}`,
    intent: isCompose ? 'Compose' : 'Browse',
    retrievalMode: isCompose ? 'Snippet' : 'Broad',
    suggestedFilters,
    includeSnippets: isCompose,
  });

  return { status: 200, body: response };
}

function normalizedContains(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

function readCatalogQueryParams(url: string): URLSearchParams {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) {
    return new URLSearchParams();
  }
  return new URLSearchParams(url.slice(queryIndex + 1));
}

function mockCatalogEntryFromAsset(
  asset: AssetAssistantAssetResultDto,
  versionNumber = 1,
): AssetCatalogEntryDto {
  const updatedAt = new Date(Date.now() - versionNumber * 86_400_000).toISOString();
  const isPublished = asset.isPublished;
  return {
    assetId: asset.assetId,
    assetVersionId: asset.assetVersionId,
    versionNumber,
    title: asset.title,
    description: asset.summary,
    type: asset.type,
    industries: mockIndustryDtosFromNames(asset.industries ?? []),
    practices: [...(asset.practices ?? [])],
    owner: asset.owner,
    versionStatus: isPublished ? 'Published' : 'Draft',
    assetStatus: isPublished ? 'Published' : 'Draft',
    isPublished,
    hasPendingAiSuggestions: asset.hasPendingAiSuggestions,
    updatedAt,
    publishedAt: isPublished ? updatedAt : null,
    hasUnpublishedDraft: asset.hasUnpublishedDraft ?? false,
    draftVersionNumber: asset.draftVersionNumber ?? null,
  };
}

function mockCatalogEntriesForVisibility(
  visibility: AssetSearchVisibilityApi,
): AssetCatalogEntryDto[] {
  const entries = MOCK_LIBRARY_CATALOG.map((row, index) =>
    mockCatalogEntryFromAsset(row.asset, index + 1),
  );

  switch (visibility) {
    case 'DraftsOnly':
      return entries.filter((entry) => !entry.isPublished);
    case 'PublishedOnly':
      return entries.filter((entry) => entry.isPublished);
    default:
      return entries;
  }
}

export function mockHandleAssetsCatalogGet(requestUrl: string): {
  status: number;
  body: AssetCatalogListResponseDto;
} {
  const params = readCatalogQueryParams(requestUrl);
  const visibilityParam = params.get('visibility') ?? 'DraftsOnly';
  const visibility = ASSET_SEARCH_VISIBILITY_OPTIONS.some(
    (option) => option.value === visibilityParam,
  )
    ? (visibilityParam as AssetSearchVisibilityApi)
    : 'IncludeDrafts';

  const pageSizeRaw = Number.parseInt(params.get('pageSize') ?? '24', 10);
  const pageSize = Number.isFinite(pageSizeRaw) && pageSizeRaw > 0 ? pageSizeRaw : 24;
  const offsetRaw = Number.parseInt(params.get('continuationToken') ?? '0', 10);
  const offset = Number.isFinite(offsetRaw) && offsetRaw >= 0 ? offsetRaw : 0;

  const ownerEmail = params.get('ownerEmail')?.trim().toLowerCase();
  let filtered = mockCatalogEntriesForVisibility(visibility);
  if (ownerEmail) {
    filtered = filtered.filter(
      (entry) => (entry.owner.email ?? '').trim().toLowerCase() === ownerEmail,
    );
  }
  const page = filtered.slice(offset, offset + pageSize);
  const nextOffset = offset + page.length;
  const continuationToken = nextOffset < filtered.length ? String(nextOffset) : null;

  return {
    status: 200,
    body: {
      visibility,
      items: page,
      continuationToken,
    },
  };
}

export function mockHandleAssetsBrowseGet(requestUrl: string): {
  status: number;
  body: {
    visibility: AssetSearchVisibilityApi;
    items: ReturnType<typeof toBrowseItem>[];
    totalCount: number;
    continuationToken: string | null;
  };
} {
  const params = readCatalogQueryParams(requestUrl);
  const visibilityParam = params.get('visibility') ?? 'PublishedOnly';
  const visibility = ASSET_SEARCH_VISIBILITY_OPTIONS.some(
    (option) => option.value === visibilityParam,
  )
    ? (visibilityParam as AssetSearchVisibilityApi)
    : 'PublishedOnly';
  const pageSize = Number.parseInt(params.get('pageSize') ?? '24', 10) || 24;
  const offset = Number.parseInt(params.get('continuationToken') ?? '0', 10) || 0;
  const types = params.getAll('types');
  const industries = params.getAll('industries');
  const practices = params.getAll('practices');
  const tags = params.getAll('tags');
  const matchAllTags = params.get('matchAllTags') === 'true';

  const matched = MOCK_LIBRARY_CATALOG.map((row) => row.asset).filter((asset) => {
    if (visibility === 'PublishedOnly' && !asset.isPublished) return false;
    if (visibility === 'DraftsOnly' && asset.isPublished) return false;
    if (types.length > 0 && !types.includes(asset.type)) return false;
    if (!selectedTagsMatch(asset.tags, tags, matchAllTags)) return false;
    if (
      practices.length > 0 &&
      !(asset.practices ?? []).some((practice) => practices.includes(practice))
    ) {
      return false;
    }
    if (
      industries.length > 0 &&
      !(asset.industries ?? []).some((industry) => industries.includes(industry))
    ) {
      return false;
    }
    return true;
  });

  const page = matched.slice(offset, offset + pageSize).map(toBrowseItem);
  const next = offset + page.length;
  return {
    status: 200,
    body: {
      visibility,
      items: page,
      totalCount: matched.length,
      continuationToken: next < matched.length ? String(next) : null,
    },
  };
}

export function mockHandleAssetsPhraseTagsPost(body: unknown): {
  status: number;
  body: { tags: string[] };
} {
  const phrase =
    typeof body === 'object' && body && 'phrase' in body ? String(body.phrase ?? '') : '';
  const normalized = phrase.trim().toLowerCase();
  if (!normalized) {
    return { status: 200, body: { tags: [] } };
  }

  const vocabulary = [...new Set(MOCK_LIBRARY_CATALOG.flatMap((row) => row.asset.tags))];
  const words = new Set(normalized.split(/\s+/).filter((word) => word.length > 0));
  const clear = vocabulary.filter(
    (tag) => normalized.includes(tag.toLowerCase()) && !words.has(tag.toLowerCase()),
  );
  const related = vocabulary.filter(
    (tag) =>
      words.has(tag.toLowerCase()) &&
      !clear.some((match) => match.toLowerCase() === tag.toLowerCase()),
  );
  return { status: 200, body: { tags: [...clear, ...related] } };
}

export function mockHandleAssetsFindPost(body: unknown): {
  status: number;
  body: { items: ReturnType<typeof toBrowseItem>[] } | { title: string; detail: string };
} {
  const query =
    typeof body === 'object' && body && 'query' in body ? String(body.query ?? '').trim() : '';
  if (!query) {
    return { status: 400, body: { title: 'Invalid request', detail: 'Query is required.' } };
  }

  const record = typeof body === 'object' && body ? (body as Record<string, unknown>) : {};
  const visibilityParam = String(record['visibility'] ?? '');
  const visibility = ASSET_SEARCH_VISIBILITY_OPTIONS.some(
    (option) => option.value === visibilityParam,
  )
    ? (visibilityParam as AssetSearchVisibilityApi)
    : 'PublishedOnly';
  const types = stringList(record['types']);
  const industries = stringList(record['industries']);
  const practices = stringList(record['practices']);
  const tags = stringList(record['tags']);
  const matchAllTags = record['matchAllTags'] === true;
  const needle = query.toLowerCase();
  const items = MOCK_LIBRARY_CATALOG.filter((row) => {
    const asset = row.asset;
    if (visibility === 'PublishedOnly' && !asset.isPublished) return false;
    if (visibility === 'DraftsOnly' && asset.isPublished) return false;
    if (types.length > 0 && !types.includes(asset.type)) return false;
    if (!selectedTagsMatch(asset.tags, tags, matchAllTags)) return false;
    if (
      practices.length > 0 &&
      !(asset.practices ?? []).some((practice) => practices.includes(practice))
    ) {
      return false;
    }
    if (
      industries.length > 0 &&
      !(asset.industries ?? []).some((industry) => industries.includes(industry))
    ) {
      return false;
    }
    return row.searchText.includes(needle) || asset.title.toLowerCase().includes(needle);
  }).map((row) => toBrowseItem(row.asset));
  return { status: 200, body: { items } };
}

function selectedTagsMatch(assetTags: string[], selected: string[], matchAll: boolean): boolean {
  if (selected.length === 0) {
    return true;
  }
  const has = (selectedTag: string) =>
    assetTags.some((tag) => selectedTag.toLowerCase() === tag.toLowerCase());
  return matchAll ? selected.every(has) : selected.some(has);
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

function toBrowseItem(asset: AssetAssistantAssetResultDto) {
  return {
    assetId: asset.assetId,
    assetVersionId: asset.assetVersionId,
    title: asset.title,
    description: asset.summary,
    type: asset.type,
    industries: asset.industries ?? [],
    practices: asset.practices ?? [],
    tags: asset.tags,
    owner: asset.owner,
    confidentiality: asset.confidentiality,
    isPublished: asset.isPublished,
    hasPendingAiSuggestions: asset.hasPendingAiSuggestions,
    updatedAt: '2026-10-01T00:00:00Z',
  };
}
